import { FiCheckSquare, FiEye, FiPhoneCall } from "react-icons/fi";
import Badge from "../common/Badge";
import EmptyState from "../common/EmptyState";
import Skeleton from "../common/Skeleton";
import {
  formatAppointmentService,
  formatDateTime,
  formatPhone,
} from "../../utils/formatters";
import { contactName } from "../../utils/labels";

function humanizeText(value, fallback = "N/A") {
  const text = String(value || "").trim();

  if (!text) {
    return fallback;
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getStatusMeta(status) {
  const normalized = String(status || "").trim().toLowerCase();
  const statusMap = {
    pending: { variant: "info", label: "Pending" },
    queued: { variant: "info", label: "Queued" },
    assigned: { variant: "blue", label: "Assigned" },
    in_progress: { variant: "purple", label: "In Progress" },
    callback_required: { variant: "purple", label: "Callback Required" },
    follow_up_pending: { variant: "gold", label: "Follow-up Pending" },
    no_answer: { variant: "requested", label: "No Answer" },
    completed: { variant: "booked", label: "Completed" },
    booked: { variant: "booked", label: "Booked" },
    not_interested: { variant: "cancelled", label: "Not Interested" },
    failed: { variant: "cancelled", label: "Failed" },
  };

  return statusMap[normalized] || {
    variant: "info",
    label: humanizeText(status, "Pending"),
  };
}

function buildCountLabel(items = []) {
  return `${items.length} call${items.length === 1 ? "" : "s"}`;
}

export default function CallingAgentWorkTable({
  title,
  items,
  loading,
  emptyTitle,
  emptySubtitle,
  onView,
  onDirectCall,
  onUpdateStatus,
}) {
  return (
    <div className="calls-section">
      <div className="section-row">
        <div className="section-title section-title-no-margin">{title}</div>
        <div className="calls-count">
          {loading ? "Loading..." : buildCountLabel(items)}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Contact Name</th>
              <th>Phone Number</th>
              <th>Service</th>
              <th>Status</th>
              <th>Updated</th>
              {/* <th>Notes / Summary</th> */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">
                  <div className="empty-state">
                    <Skeleton style={{ height: 14, width: 180, margin: "0 auto 8px" }} />
                    <Skeleton style={{ height: 10, width: 120, margin: "0 auto" }} />
                  </div>
                </td>
              </tr>
            ) : !items.length ? (
              <tr>
                <td colSpan="7">
                  <EmptyState
                    title={emptyTitle}
                    subtitle={emptySubtitle}
                  />
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const statusMeta = getStatusMeta(item.status || item.call_status);

                return (
                  <tr key={item.call_id || item.call_uuid}>
                    <td>{contactName(item.patient_name)}</td>
                    <td className="td-phone">{formatPhone(item.phone)}</td>
                    <td>{formatAppointmentService(item.service_type || item.service_interest)}</td>
                    <td>
                      <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
                    </td>
                    <td>{formatDateTime(item.updated_at || item.assigned_at || item.created_at)}</td>
                    {/* <td className="td-summary">
                      <div>{notesText}</div>
                      <div className="muted-text">
                        {humanizeText(item.source, "Manual Assignment")}
                        {item.booking_status
                          ? ` • ${humanizeText(item.booking_status)}`
                          : ""}
                      </div>
                    </td> */}
                    <td className="td-actions">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title={`View details for ${item.patient_name || "call"}`}
                          aria-label={`View details for ${item.patient_name || "call"}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                          onClick={() => onView(item)}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          title={`Trigger direct call for ${item.patient_name || "call"}`}
                          aria-label={`Trigger direct call for ${item.patient_name || "call"}`}
                          className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                          onClick={() => onDirectCall(item)}
                        >
                          <FiPhoneCall />
                          <span>Call</span>
                        </button>
                        <button
                          type="button"
                          title={`Update status for ${item.patient_name || "call"}`}
                          aria-label={`Update status for ${item.patient_name || "call"}`}
                          className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                          onClick={() => onUpdateStatus(item)}
                        >
                          <FiCheckSquare />
                          <span>Update Status</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
